import { useCallback, useMemo } from 'react';

import FullPageWithPDFViewer from '~/common/componentMapper/FullPageWithPDFViewer/FullPageWithPDFViewerComponent';
import {
  StickyPdfColumn,
  StyledGridItem,
  StyledGridItemFullHeight,
} from '~/common/components/FullPageWithPDFViewer/splitColumns';
import GlobalLayout from '~/common/components/GlobalLayout';
import PDFViewerWithIframe from '~/common/components/PDFViewerWithIframe/PDFViewerWithIframe';
import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';

import PageHeader from '../../GlobalLayout/PageHeader';
import QuestionLibrary from './../index';

type GlobalLayoutWithQuestionLibraryProps = {
  children?: any;
  as?: React.ElementType;
  handleSubmit?: (...args: any[]) => any;
  handleNextButton?: (...args: any[]) => any;
  customHeader?: React.ReactNode;
  error?: boolean;
  showOops?: boolean;
  heading?: string;
  isLoading?: boolean;
  loadingMessage?: string;
  primaryButtonId?: string;
  headerOptions?: {
    title?: string;
    isTitleHidden?: boolean;
    isAiSwitchHidden?: boolean;
    isAiSwitchButtonEnabled?: boolean;
    isHidden?: boolean;
  };
  primaryButton?: {
    id?: string;
    name?: string;
    onSubmit?: (...args: any[]) => any;
    isDisabled?: boolean;
    isSubmitting?: boolean;
    'data-testid'?: string;
  };
  secondaryButton?: {
    name: string;
    onSubmit: (...args: any[]) => any;
    isDisabled?: boolean;
    isSubmitting?: boolean;
    'data-testid'?: string;
  };

  subHeading?: string;
  customStyles?: {
    main?: string;
  };
  isDirty?: boolean;
  subHeadingStyles?: any;
  waitForSignalR?: boolean;
  auditHistoryData?: any;
  shouldAutoCheck?: boolean;
  documentId?: string | number;
  documentOptions?: any[];
  gridTemplateColumns?: string;
  formMethods?: any;
  screenData?: any;
  initialFormValues?: any;
  sideEffects?: React.ComponentType[];
  showPdfViewer?: boolean;
  questions?: any[];
  errorMessage?: string;
  methods?: {
    getValues?: (...args: any[]) => any;
    trigger?: (...args: any[]) => any;
    formState?: any;
  };
  hasNewBackground?: boolean;
  maxHeight?: boolean;
  shouldUpdateScreenData?: boolean;
  fullPageHeight?: boolean;
};

export default function GlobalLayoutWithQuestionLibrary({
  sideEffects = [],
  screenData,
  documentId,
  documentOptions,
  showPdfViewer = true,
  headerOptions = {},
  gridTemplateColumns = showPdfViewer ? '60% 40%' : '100%',
  questions,
  error,
  errorMessage,
  isLoading,
  primaryButton,
  methods,
  hasNewBackground,
  maxHeight,
  shouldUpdateScreenData = false,
  fullPageHeight,
  ...rest
}: Readonly<GlobalLayoutWithQuestionLibraryProps>) {
  const showSnackbar = useSnackbar();
  const { trigger, getValues } = methods;
  const {
    title = '',
    isTitleHidden = false,
    isAiSwitchHidden = false,
    isAiSwitchButtonEnabled = false,
    isHidden = false,
  } = headerOptions;

  const handleValidationError = useCallback(async () => {
    showSnackbar({
      message: 'Please fill out all required fields',
      severity: 'error',
      duration: 5000,
    });

    return false;
  }, [showSnackbar]);

  const GridItem = fullPageHeight ? StyledGridItemFullHeight : StyledGridItem;

  const updatedPrimaryButton = useMemo(
    () => ({
      ...primaryButton,
      onSubmit: async () =>
        (await trigger())
          ? primaryButton.onSubmit(getValues())
          : handleValidationError(),
    }),
    [primaryButton, trigger, getValues, handleValidationError],
  );

  return (
    <GlobalLayout
      customHeader={
        !isHidden && (
          <PageHeader
            title={title}
            isTitleHidden={isTitleHidden}
            isAiSwitchButtonEnabled={
              isAiSwitchButtonEnabled && !isAiSwitchHidden
            }
          />
        )
      }
      error={error ? errorMessage : null}
      isLoading={isLoading}
      primaryButton={updatedPrimaryButton}
      hasNewBackground={hasNewBackground}
      isHeaderHidden={isHidden}
      forceFullPage={showPdfViewer}
      {...rest}
    >
      <FullPageWithPDFViewer
        gridTemplateColumns={gridTemplateColumns}
        showSplitScreen={showPdfViewer ? 'true' : 'false'}
        height={'100%'}
      >
        {showPdfViewer && (
          <StickyPdfColumn data-testid="pdfViewerColumn">
            <PDFViewerWithIframe
              defaultDocumentId={`${documentId}`}
              documentOptions={documentOptions}
            />
          </StickyPdfColumn>
        )}
        <GridItem
          data-testid="questionLibraryColumn"
          sx={{
            paddingLeft: '1rem',
            paddingRight: '1rem',
            paddingTop: isHidden ? '24px' : undefined,
            maxHeight: maxHeight ? '100%' : undefined,
          }}
        >
          <QuestionLibrary
            formMethods={methods}
            sideEffects={sideEffects}
            screenData={screenData}
            questions={questions}
            shouldUpdateScreenData={shouldUpdateScreenData}
            isAiSwitchButtonEnabled={isAiSwitchButtonEnabled}
          />
        </GridItem>
      </FullPageWithPDFViewer>
    </GlobalLayout>
  );
}
